import React from "react";
import { useSelector } from "react-redux";
import { FiPlus, FiX, FiInfo } from "react-icons/fi";
import {
  PRODUCT_TYPE_ENUM,
  PRODUCT_SUBTYPE_ENUM,
  PURITY_ENUM,
  COLLECTION_TYPE_ENUM,
} from "../../../constants/product.constant";

// ============= BASIC INFO SECTION =============
// Add this at the top of the BasicInfoSection
export const BasicInfoSection = ({ formData, errors, onChange, mode }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-4 text-text">Basic Information</h3>

    {mode === "clone" && (
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Cloning:</strong> This will create a new rule based on the
          selected rule. The new rule will be inactive by default.
        </p>
      </div>
    )}

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Rule Name *
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={onChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.name ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="e.g., Gold Ring Making Charges - Mumbai"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-500">{errors.name}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Enter a descriptive name for this pricing rule
        </p>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={onChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Enter rule description (optional)"
        />
        <p className="mt-1 text-xs text-gray-500">
          Provide additional details about when and how this rule should be
          applied
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Priority *
        </label>
        <input
          type="number"
          name="priority"
          value={formData.priority}
          onChange={onChange}
          min="0"
          max="100"
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.priority ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="0"
        />
        {errors.priority && (
          <p className="mt-1 text-sm text-red-500">{errors.priority}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Higher priority rules (e.g., 10) are evaluated before lower priority
          rules (e.g., 1)
        </p>
      </div>

      <div className="flex items-center pt-7">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            Active Rule
          </span>
        </label>
        <p className="ml-2 text-xs text-gray-500">
          {formData.isActive
            ? "Rule will be applied immediately"
            : "Rule is inactive and won't be applied"}
        </p>
      </div>
    </div>
  </div>
);

// ============= CONDITIONS SECTION =============
export const ConditionsSection = ({
  formData,
  errors,
  onChange,
  onArrayChange,
  branches = [],
  categories = [],
}) => {
  return (
    <div className="space-y-6">
      {/* Product Conditions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4 text-text">
          Product Conditions
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Product Types */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Types
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {PRODUCT_TYPE_ENUM.map((type) => (
                <label key={type} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.conditions.productTypes.includes(type)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...formData.conditions.productTypes, type]
                        : formData.conditions.productTypes.filter(
                            (t) => t !== type
                          );
                      onArrayChange("conditions.productTypes", newTypes);
                    }}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">{type}</span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to apply to all product types
            </p>
          </div>

          {/* Sub Types */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sub Types
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded p-2">
              {PRODUCT_SUBTYPE_ENUM.map((subType) => (
                <label key={subType} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.conditions.subTypes.includes(subType)}
                    onChange={(e) => {
                      const newSubTypes = e.target.checked
                        ? [...formData.conditions.subTypes, subType]
                        : formData.conditions.subTypes.filter(
                            (t) => t !== subType
                          );
                      onArrayChange("conditions.subTypes", newSubTypes);
                    }}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">{subType}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Purity Levels */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Purity Levels
            </label>
            <div className="flex flex-wrap gap-2">
              {PURITY_ENUM.map((purity) => (
                <label key={purity} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.conditions.purityLevels.includes(purity)}
                    onChange={(e) => {
                      const newPurities = e.target.checked
                        ? [...formData.conditions.purityLevels, purity]
                        : formData.conditions.purityLevels.filter(
                            (p) => p !== purity
                          );
                      onArrayChange("conditions.purityLevels", newPurities);
                    }}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">{purity}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Collections */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Collections
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {COLLECTION_TYPE_ENUM.map((collection) => (
                <label key={collection} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.conditions.collections.includes(
                      collection
                    )}
                    onChange={(e) => {
                      const newCollections = e.target.checked
                        ? [...formData.conditions.collections, collection]
                        : formData.conditions.collections.filter(
                            (c) => c !== collection
                          );
                      onArrayChange("conditions.collections", newCollections);
                    }}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {collection}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Weight Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Weight (grams)
            </label>
            <input
              type="number"
              name="conditions.minWeight"
              value={formData.conditions.minWeight}
              onChange={onChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Weight (grams)
            </label>
            <input
              type="number"
              name="conditions.maxWeight"
              value={formData.conditions.maxWeight}
              onChange={onChange}
              min="0"
              step="0.01"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors["conditions.maxWeight"]
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="0.00"
            />
            {errors["conditions.maxWeight"] && (
              <p className="mt-1 text-sm text-red-500">
                {errors["conditions.maxWeight"]}
              </p>
            )}
          </div>

          {/* Quantity Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Quantity
            </label>
            <input
              type="number"
              name="conditions.minQuantity"
              value={formData.conditions.minQuantity}
              onChange={onChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Quantity
            </label>
            <input
              type="number"
              name="conditions.maxQuantity"
              value={formData.conditions.maxQuantity}
              onChange={onChange}
              min="0"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors["conditions.maxQuantity"]
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="0"
            />
            {errors["conditions.maxQuantity"] && (
              <p className="mt-1 text-sm text-red-500">
                {errors["conditions.maxQuantity"]}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Location & Customer Conditions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4 text-text">
          Location & Customer Conditions
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Branch Codes */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Applicable Branches
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded p-2">
              {branches.map((branch) => (
                <label key={branch.code} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.conditions.branchCodes.includes(
                      branch.code
                    )}
                    onChange={(e) => {
                      const newBranches = e.target.checked
                        ? [...formData.conditions.branchCodes, branch.code]
                        : formData.conditions.branchCodes.filter(
                            (b) => b !== branch.code
                          );
                      onArrayChange("conditions.branchCodes", newBranches);
                    }}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {branch.name}
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to apply to all branches
            </p>
          </div>

          {/* Customer Types */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Types
            </label>
            <div className="flex flex-wrap gap-4">
              {["RETAIL", "WHOLESALE", "VIP"].map((type) => (
                <label key={type} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.conditions.customerTypes.includes(type)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...formData.conditions.customerTypes, type]
                        : formData.conditions.customerTypes.filter(
                            (t) => t !== type
                          );
                      onArrayChange("conditions.customerTypes", newTypes);
                    }}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">{type}</span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to apply to all customer types
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= CHARGES SECTION =============
export const ChargesSection = ({
  formData,
  errors,
  onChange,
  onSlabChange,
  onAddSlab,
  onRemoveSlab,
  onTierChange,
  onAddTier,
  onRemoveTier,
  onAdditionalChargeChange,
  onAddAdditionalCharge,
  onRemoveAdditionalCharge,
}) => (
  <div className="space-y-6">
    {/* Making Charges */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4 text-text">Making Charges</h3>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Charge Type *
            </label>
            <select
              name="charges.makingCharges.type"
              value={formData.charges.makingCharges.type}
              onChange={onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="PERCENTAGE">Percentage</option>
              <option value="PER_GRAM">Per Gram</option>
              <option value="FIXED">Fixed Amount</option>
              <option value="SLAB">Slab-based</option>
              <option value="TIERED">Tiered</option>
            </select>
          </div>

          {!["SLAB", "TIERED"].includes(
            formData.charges.makingCharges.type
          ) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value *
              </label>
              <input
                type="number"
                name="charges.makingCharges.value"
                value={formData.charges.makingCharges.value}
                onChange={onChange}
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors["charges.makingCharges.value"]
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="0.00"
              />
              {errors["charges.makingCharges.value"] && (
                <p className="mt-1 text-sm text-red-500">
                  {errors["charges.makingCharges.value"]}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Amount
            </label>
            <input
              type="number"
              name="charges.makingCharges.minAmount"
              value={formData.charges.makingCharges.minAmount}
              onChange={onChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Amount
            </label>
            <input
              type="number"
              name="charges.makingCharges.maxAmount"
              value={formData.charges.makingCharges.maxAmount}
              onChange={onChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Slabs */}
        {formData.charges.makingCharges.type === "SLAB" && (
          <SlabsManager
            slabs={formData.charges.makingCharges.slabs}
            errors={errors}
            onSlabChange={onSlabChange}
            onAddSlab={onAddSlab}
            onRemoveSlab={onRemoveSlab}
          />
        )}

        {/* Tiers */}
        {formData.charges.makingCharges.type === "TIERED" && (
          <TiersManager
            tiers={formData.charges.makingCharges.tiers}
            errors={errors}
            onTierChange={onTierChange}
            onAddTier={onAddTier}
            onRemoveTier={onRemoveTier}
          />
        )}
      </div>
    </div>

    {/* Wastage Charges */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4 text-text">Wastage Charges</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Charge Type
          </label>
          <select
            name="charges.wastageCharges.type"
            value={formData.charges.wastageCharges.type}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="PERCENTAGE">Percentage</option>
            <option value="FIXED">Fixed Amount</option>
            <option value="PER_GRAM">Per Gram</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Value
          </label>
          <input
            type="number"
            name="charges.wastageCharges.value"
            value={formData.charges.wastageCharges.value}
            onChange={onChange}
            min="0"
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="0.00"
          />
        </div>

        <div className="md:col-span-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="charges.wastageCharges.includeStoneWeight"
              checked={formData.charges.wastageCharges.includeStoneWeight}
              onChange={onChange}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="ml-2 text-sm text-gray-700">
              Include Stone Weight in Wastage Calculation
            </span>
          </label>
        </div>
      </div>
    </div>

    {/* Stone Charges */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4 text-text">Stone Charges</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Charge Type
          </label>
          <select
            name="charges.stoneCharges.type"
            value={formData.charges.stoneCharges.type}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="PER_CARAT">Per Carat</option>
            <option value="FIXED">Fixed Amount</option>
            <option value="PERCENTAGE">Percentage</option>
            <option value="MARKET_RATE">Market Rate</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Value
          </label>
          <input
            type="number"
            name="charges.stoneCharges.value"
            value={formData.charges.stoneCharges.value}
            onChange={onChange}
            min="0"
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="0.00"
          />
        </div>

        <div className="md:col-span-2">
          <label className="flex items-center mb-2">
            <input
              type="checkbox"
              name="charges.stoneCharges.useMarketRate"
              checked={formData.charges.stoneCharges.useMarketRate}
              onChange={onChange}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="ml-2 text-sm text-gray-700">Use Market Rate</span>
          </label>

          {formData.charges.stoneCharges.useMarketRate && (
            <input
              type="number"
              name="charges.stoneCharges.marginOnMarketRate"
              value={formData.charges.stoneCharges.marginOnMarketRate}
              onChange={onChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Margin %"
            />
          )}
        </div>
      </div>
    </div>

    {/* Hallmark Charges */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4 text-text">Hallmark Charges</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="charges.hallmarkCharges.enabled"
              checked={formData.charges.hallmarkCharges.enabled}
              onChange={onChange}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">
              Enable Hallmark Charges
            </span>
          </label>
        </div>

        {formData.charges.hallmarkCharges.enabled && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Charge Type
              </label>
              <select
                name="charges.hallmarkCharges.type"
                value={formData.charges.hallmarkCharges.type}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="FIXED">Fixed Amount</option>
                <option value="PER_GRAM">Per Gram</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value
              </label>
              <input
                type="number"
                name="charges.hallmarkCharges.value"
                value={formData.charges.hallmarkCharges.value}
                onChange={onChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="50.00"
              />
            </div>
          </>
        )}
      </div>
    </div>

    {/* GST Charges */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4 text-text">GST Charges</h3>

      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <FiInfo className="text-primary" />
          <p className="text-sm text-neutral-dark">
            Configure GST rate and what should be included in GST calculation
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              GST Rate (%) *
            </label>
            <select
              name="charges.gstCharges.rate"
              value={formData.charges.gstCharges.rate}
              onChange={onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value={0.25}>0.25%</option>
              <option value={3}>3%</option>
              <option value={5}>5%</option>
              <option value={12}>12%</option>
              <option value={18}>18%</option>
              <option value={28}>28%</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Standard rates: 3% for gold, 5% for silver
            </p>
          </div>

          <div className="space-y-3 md:pt-7">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="charges.gstCharges.includesMaking"
                checked={formData.charges.gstCharges.includesMaking}
                onChange={onChange}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">
                Include Making Charges in GST
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="charges.gstCharges.includesWastage"
                checked={formData.charges.gstCharges.includesWastage}
                onChange={onChange}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">
                Include Wastage Charges in GST
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="charges.gstCharges.includesStones"
                checked={formData.charges.gstCharges.includesStones}
                onChange={onChange}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">
                Include Stone Charges in GST
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>

    {/* Additional Charges */}
    <AdditionalChargesManager
      charges={formData.charges.additionalCharges}
      onChargeChange={onAdditionalChargeChange}
      onAddCharge={onAddAdditionalCharge}
      onRemoveCharge={onRemoveAdditionalCharge}
    />
  </div>
);

// Slabs Manager Component
const SlabsManager = ({
  slabs,
  errors,
  onSlabChange,
  onAddSlab,
  onRemoveSlab,
}) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <div className="flex justify-between items-center mb-4">
      <h4 className="font-medium text-text">Weight Slabs</h4>
      <button
        type="button"
        onClick={onAddSlab}
        className="flex items-center text-sm text-primary hover:text-primary-dark"
      >
        <FiPlus className="mr-1" />
        Add Slab
      </button>
    </div>

    {errors["charges.makingCharges.slabs"] && (
      <p className="text-sm text-red-500 mb-2">
        {errors["charges.makingCharges.slabs"]}
      </p>
    )}

    <div className="space-y-3">
      {slabs.map((slab, index) => (
        <div
          key={index}
          className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg"
        >
          <input
            type="number"
            value={slab.minWeight}
            onChange={(e) => onSlabChange(index, "minWeight", e.target.value)}
            placeholder="Min Weight"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            type="number"
            value={slab.maxWeight}
            onChange={(e) => onSlabChange(index, "maxWeight", e.target.value)}
            placeholder="Max Weight"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <select
            value={slab.type}
            onChange={(e) => onSlabChange(index, "type", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="PERCENTAGE">Percentage</option>
            <option value="PER_GRAM">Per Gram</option>
            <option value="FIXED">Fixed</option>
          </select>
          <div className="flex gap-2">
            <input
              type="number"
              value={slab.value}
              onChange={(e) => onSlabChange(index, "value", e.target.value)}
              placeholder="Value"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <button
              type="button"
              onClick={() => onRemoveSlab(index)}
              className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
            >
              <FiX />
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Tiers Manager Component
const TiersManager = ({
  tiers,
  errors,
  onTierChange,
  onAddTier,
  onRemoveTier,
}) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <div className="flex justify-between items-center mb-4">
      <h4 className="font-medium text-text">Quantity Tiers</h4>
      <button
        type="button"
        onClick={onAddTier}
        className="flex items-center text-sm text-primary hover:text-primary-dark"
      >
        <FiPlus className="mr-1" />
        Add Tier
      </button>
    </div>

    {errors["charges.makingCharges.tiers"] && (
      <p className="text-sm text-red-500 mb-2">
        {errors["charges.makingCharges.tiers"]}
      </p>
    )}

    <div className="space-y-3">
      {tiers.map((tier, index) => (
        <div
          key={index}
          className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg"
        >
          <input
            type="number"
            value={tier.minQuantity}
            onChange={(e) => onTierChange(index, "minQuantity", e.target.value)}
            placeholder="Min Quantity"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            type="number"
            value={tier.maxQuantity}
            onChange={(e) => onTierChange(index, "maxQuantity", e.target.value)}
            placeholder="Max Quantity"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <div className="flex gap-2 md:col-span-2">
            <input
              type="number"
              value={tier.value}
              onChange={(e) => onTierChange(index, "value", e.target.value)}
              placeholder="Discount %"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <button
              type="button"
              onClick={() => onRemoveTier(index)}
              className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
            >
              <FiX />
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Additional Charges Manager
const AdditionalChargesManager = ({
  charges,
  onChargeChange,
  onAddCharge,
  onRemoveCharge,
}) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-medium text-text">Additional Charges</h3>
      <button
        type="button"
        onClick={onAddCharge}
        className="flex items-center text-sm text-primary hover:text-primary-dark"
      >
        <FiPlus className="mr-1" />
        Add Charge
      </button>
    </div>

    <div className="space-y-4">
      {charges.map((charge, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <input
                type="text"
                value={charge.name}
                onChange={(e) => onChargeChange(index, "name", e.target.value)}
                placeholder="Charge Name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            <select
              value={charge.type}
              onChange={(e) => onChargeChange(index, "type", e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="PERCENTAGE">Percentage</option>
              <option value="FIXED">Fixed</option>
              <option value="PER_GRAM">Per Gram</option>
            </select>

            <input
              type="number"
              value={charge.value}
              onChange={(e) => onChargeChange(index, "value", e.target.value)}
              placeholder="Value"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />

            <select
              value={charge.appliesTo}
              onChange={(e) =>
                onChargeChange(index, "appliesTo", e.target.value)
              }
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="TOTAL">Total Amount</option>
              <option value="METAL_VALUE">Metal Value</option>
              <option value="MAKING_CHARGES">Making Charges</option>
            </select>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={charge.isOptional}
                  onChange={(e) =>
                    onChargeChange(index, "isOptional", e.target.checked)
                  }
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm text-gray-700">Optional</span>
              </label>

              <button
                type="button"
                onClick={() => onRemoveCharge(index)}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
              >
                <FiX />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============= GST CONFIG SECTION =============
export const GSTConfigSection = ({ formData, errors, onChange }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-4 text-text">GST Configuration</h3>

    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <FiInfo className="text-primary" />
        <p className="text-sm text-neutral-dark">
          Configure which components should be included in GST calculation
        </p>
      </div>

      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            name="gstConfig.includesMaking"
            checked={formData.gstConfig.includesMaking}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm text-gray-700">
            Include Making Charges in GST
          </span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="gstConfig.includesWastage"
            checked={formData.gstConfig.includesWastage}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm text-gray-700">
            Include Wastage Charges in GST
          </span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="gstConfig.includesStones"
            checked={formData.gstConfig.includesStones}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm text-gray-700">
            Include Stone Charges in GST
          </span>
        </label>
      </div>

      <div className="pt-4 border-t">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Custom GST Rate (%)
        </label>
        <input
          type="number"
          name="gstConfig.customRate"
          value={formData.gstConfig.customRate}
          onChange={onChange}
          min="0"
          max="100"
          step="0.01"
          className="w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Leave empty for default rate"
        />
        <p className="mt-1 text-xs text-gray-500">
          Leave empty to use default GST rate (3% for gold, 5% for silver)
        </p>
      </div>
    </div>
  </div>
);

// ============= VALIDITY SECTION =============
export const ValiditySection = ({ formData, errors, onChange }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-4 text-text">Validity Period</h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Valid From *
        </label>
        <input
          type="date"
          name="validFrom"
          value={formData.validFrom}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <p className="mt-1 text-xs text-gray-500">
          When this rule becomes active
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Valid Until
        </label>
        <input
          type="date"
          name="validUntil"
          value={formData.validUntil}
          onChange={onChange}
          min={formData.validFrom}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.validUntil ? "border-red-500" : "border-gray-300"
          }`}
        />
        <p className="mt-1 text-xs text-gray-500">
          Leave empty for no expiry date
        </p>
        {errors.validUntil && (
          <p className="mt-1 text-sm text-red-500">{errors.validUntil}</p>
        )}
      </div>
    </div>

    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-start">
        <FiInfo className="text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Rule Application</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Rules with higher priority are evaluated first</li>
            <li>First matching rule will be applied to products</li>
            <li>Inactive rules will not be considered</li>
            <li>Expired rules are automatically skipped</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);
